/**
 * AdminWorkOrders - Simplified Work Orders List (P1 Contract: One WO per Order)
 * 
 * Features:
 * - Simple list view (no department board)
 * - Default sort: newest first (updated_at desc)
 * - Columns: work_order_number, order_id, status, department (optional), updated_at
 * - Status filter, optional department filter
 */
import React, { useState, useEffect } from 'react';
import { operationsAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Wrench, 
  Clock, 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  Eye,
  Play,
  Search,
  RefreshCw,
  Loader2
} from 'lucide-react';

// Simplified statuses (P1 Contract: no BLOCKED)
const STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'done', label: 'Done', color: 'bg-green-100 text-green-700' },
];

// Optional department filter (department is now optional)
const DEPARTMENTS = [
  { value: 'art', label: 'Art' },
  { value: 'engraving', label: 'Engraving' },
  { value: 'printing', label: 'Printing' },
  { value: 'large_format', label: 'Large Format' },
  { value: 'laser', label: 'Laser' },
  { value: 'shipping', label: 'Shipping' },
];

export default function AdminWorkOrders({ onViewWorkOrder, onViewOrder }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ department: '', status: '' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchWorkOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (filter.department) params.department = filter.department;
      if (filter.status) params.status = filter.status;
      
      const data = await operationsAPI.getWorkOrders(params);
      setWorkOrders(data.work_orders || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch work orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search locally (work_order_number or order_id)
  const filteredWorkOrders = workOrders.filter(wo => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      wo.work_order_number?.toLowerCase().includes(searchLower) ||
      wo.order_id?.toLowerCase().includes(searchLower) ||
      wo.order_info?.customer_name?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusStyle = (status) => {
    const found = STATUSES.find(s => s.value === status);
    return found ? found.color : 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const found = STATUSES.find(s => s.value === status);
    return found ? found.label : status;
  };

  const getDeptLabel = (dept) => {
    if (!dept) return '—';
    const found = DEPARTMENTS.find(d => d.value === dept);
    return found ? found.label : dept;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOverdue = (wo) => {
    return wo.status !== 'done' && wo.due_at && new Date(wo.due_at) < new Date();
  };

  // Stats
  const stats = {
    total: total,
    open: workOrders.filter(wo => wo.status === 'open').length,
    inProgress: workOrders.filter(wo => wo.status === 'in_progress').length,
    done: workOrders.filter(wo => wo.status === 'done').length,
    overdue: workOrders.filter(wo => isOverdue(wo)).length,
  };

  return (
    <div className="p-6" data-testid="admin-work-orders-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
        <p className="text-gray-600">Production workflow management (one work order per order)</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-total">
          <div className="flex items-center gap-3">
            <Wrench className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-open">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.open}</p>
              <p className="text-sm text-gray-600">Open</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-in-progress">
          <div className="flex items-center gap-3">
            <Play className="w-5 h-5 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
              <p className="text-sm text-gray-600">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-done">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.done}</p>
              <p className="text-sm text-gray-600">Done</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-overdue">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              <p className="text-sm text-gray-600">Overdue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search WO#, Order#, Customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="wo-search"
            />
          </div>

          {/* Status Filter */}
          <Select 
            value={filter.status || "all"} 
            onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}
          >
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Department Filter (Optional) */}
          <Select 
            value={filter.department || "all"} 
            onValueChange={(v) => setFilter({ ...filter, department: v === "all" ? "" : v })}
          >
            <SelectTrigger className="w-40" data-testid="dept-filter">
              <SelectValue placeholder="All Depts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {DEPARTMENTS.map(d => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Refresh */}
          <Button variant="outline" size="icon" onClick={fetchWorkOrders} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Work Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredWorkOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No work orders found</p>
          </div>
        ) : (
          <Table data-testid="work-orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>Work Order #</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWorkOrders.map((wo) => (
                <TableRow 
                  key={wo.id}
                  className={`${isOverdue(wo) ? 'bg-red-50' : ''} cursor-pointer hover:bg-gray-50`}
                  onClick={() => onViewWorkOrder?.(wo.id)}
                  data-testid={`wo-row-${wo.work_order_number}`}
                >
                  <TableCell className="font-mono font-medium">
                    <div className="flex items-center gap-2">
                      {wo.work_order_number}
                      {isOverdue(wo) && (
                        <AlertTriangle className="w-4 h-4 text-red-500" title="Overdue" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <button 
                      className="font-mono text-blue-600 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewOrder?.(wo.order_id);
                      }}
                    >
                      {wo.order_id}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {wo.order_info?.customer_name || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusStyle(wo.status)}>
                      {getStatusLabel(wo.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 capitalize">
                    {getDeptLabel(wo.department)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {formatDate(wo.updated_at)}
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewWorkOrder?.(wo.id);
                      }}
                      data-testid={`view-wo-${wo.work_order_number}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        
        {/* Footer with count */}
        {!loading && filteredWorkOrders.length > 0 && (
          <div className="px-4 py-3 border-t text-sm text-gray-500">
            Showing {filteredWorkOrders.length} of {total} work orders
          </div>
        )}
      </div>
    </div>
  );
}
